// Serviço de preparação de consulta (jornada instanciada + prontidão explicável).
import { createClient } from '@/lib/supabase/client'
import type { PreparacaoItem, ProtocoloVersao } from '@/lib/types'
import { registrarAuditoria } from '@/services/identity'
import { registrarEvento } from '@/services/timeline'
import { encontrarProtocoloParaConsulta } from '@/services/protocolos'

export interface Preparacao {
  id: string
  consulta_id: string
  paciente_id: string
  protocolo_versao_id: string | null
  itens: PreparacaoItem[]
}

export async function getPreparacao(workspaceId: string, consultaId: string): Promise<Preparacao | null> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('preparacoes')
    .select('id, consulta_id, paciente_id, protocolo_versao_id, preparacao_itens(*)')
    .eq('workspace_id', workspaceId)
    .eq('consulta_id', consultaId)
    .maybeSingle()
  if (error) throw error
  if (!data) return null
  const row = data as Record<string, unknown>
  const itens = ((row.preparacao_itens as PreparacaoItem[] | null) ?? [])
    .sort((a, b) => a.created_at.localeCompare(b.created_at))
  const { preparacao_itens: _drop, ...resto } = row
  void _drop
  return { ...(resto as unknown as Omit<Preparacao, 'itens'>), itens }
}

/** Inicia a jornada de preparação da consulta a partir do protocolo aplicável. */
export async function iniciarPreparacao(params: {
  workspaceId: string
  consultaId: string
  pacienteId: string
  tipo: 'primeira' | 'retorno'
}): Promise<Preparacao | null> {
  const match = await encontrarProtocoloParaConsulta({ workspaceId: params.workspaceId, tipo: params.tipo })
  if (!match) return null
  return instanciar(params, match.versao)
}

async function instanciar(
  params: { workspaceId: string; consultaId: string; pacienteId: string },
  versao: ProtocoloVersao
): Promise<Preparacao | null> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('preparacoes')
    .insert({
      workspace_id: params.workspaceId,
      consulta_id: params.consultaId,
      paciente_id: params.pacienteId,
      protocolo_versao_id: versao.id,
    })
    .select('id')
    .single()
  if (error) {
    // já existe (unique consulta_id) — recarrega
    return getPreparacao(params.workspaceId, params.consultaId)
  }
  const prepId = (data as { id: string }).id

  if (versao.itens.length > 0) {
    const { error: e2 } = await supabase.from('preparacao_itens').insert(
      versao.itens.map(item => ({
        workspace_id: params.workspaceId,
        preparacao_id: prepId,
        item_ref: item.id,
        tipo: item.tipo,
        titulo: item.titulo,
        obrigatorio: item.obrigatorio,
        bloqueia: item.bloqueia,
      }))
    )
    if (e2) throw e2
  }

  await registrarEvento({
    workspaceId: params.workspaceId,
    pacienteId: params.pacienteId,
    consultaId: params.consultaId,
    tipo: 'preparacao_iniciada',
    titulo: 'Preparação da consulta iniciada',
    eventoDominio: 'preparation.started',
  })
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'preparacao_iniciada',
    entidade: 'preparacoes',
    registroId: prepId,
  })
  return getPreparacao(params.workspaceId, params.consultaId)
}

export async function concluirItem(params: {
  workspaceId: string
  item: PreparacaoItem
  pacienteId: string
  consultaId: string
}): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase
    .from('preparacao_itens')
    .update({ status: 'concluido', respondido_por: 'equipe', concluido_em: new Date().toISOString() })
    .eq('id', params.item.id)
    .eq('workspace_id', params.workspaceId)
  if (error) throw error
  await registrarEvento({
    workspaceId: params.workspaceId,
    pacienteId: params.pacienteId,
    consultaId: params.consultaId,
    tipo: 'preparacao_item',
    titulo: `Item de preparação concluído: ${params.item.titulo}`,
  })
}

export async function dispensarItem(params: {
  workspaceId: string
  item: PreparacaoItem
  motivo: string
  pacienteId: string
  consultaId: string
}): Promise<void> {
  if (!params.motivo.trim()) throw new Error('Dispensa exige motivo.')
  const supabase = createClient()
  const { error } = await supabase
    .from('preparacao_itens')
    .update({ status: 'dispensado', dispensado_motivo: params.motivo.trim() })
    .eq('id', params.item.id)
    .eq('workspace_id', params.workspaceId)
  if (error) throw error
  await registrarEvento({
    workspaceId: params.workspaceId,
    pacienteId: params.pacienteId,
    consultaId: params.consultaId,
    tipo: 'preparacao_item',
    titulo: `Item de preparação dispensado: ${params.item.titulo} (${params.motivo.trim()})`,
  })
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'preparacao_item_dispensado',
    entidade: 'preparacao_itens',
    registroId: params.item.id,
    dados: { motivo: params.motivo.trim() },
  })
}

export async function reabrirItem(params: { workspaceId: string; itemId: string }): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase
    .from('preparacao_itens')
    .update({ status: 'pendente', dispensado_motivo: null, concluido_em: null })
    .eq('id', params.itemId)
    .eq('workspace_id', params.workspaceId)
  if (error) throw error
}

function gerarToken(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

/** Gera link público de pré-anamnese para o paciente (token forte, expira em 14 dias). */
export async function gerarLinkPreAnamnese(params: {
  workspaceId: string
  pacienteId: string
  consultaId: string
  preparacaoItemId: string
}): Promise<string> {
  const supabase = createClient()
  const token = gerarToken()
  const { error } = await supabase.from('links_paciente').insert({
    workspace_id: params.workspaceId,
    paciente_id: params.pacienteId,
    consulta_id: params.consultaId,
    preparacao_item_id: params.preparacaoItemId,
    escopo: 'pre_anamnese',
    token,
  })
  if (error) throw error
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'link_paciente_gerado',
    entidade: 'links_paciente',
    dados: { escopo: 'pre_anamnese' },
  })
  return `/p/${token}`
}

/** Motivos pelos quais a consulta não está pronta (prontidão explicável). */
export function motivosPendentes(prep: Preparacao | null): string[] {
  if (!prep) return ['Preparação ainda não iniciada']
  return prep.itens
    .filter(i => i.status === 'pendente')
    .map(i => `${i.titulo}${i.bloqueia ? ' (bloqueia a consulta)' : i.obrigatorio ? ' (obrigatório)' : ''}`)
}
